import { cn } from '@/lib/utils/cn';
import { useSurveyStore } from '@/stores/survey-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { useResponseStore } from '@/stores/response-store';
import { isAnswered } from '@/lib/utils/answer';

interface ProgressBarProps {
  className?: string;
}

export function ProgressBar({ className }: ProgressBarProps) {
  const { settings, questions } = useSurveyStore();
  const { currentPage, totalPages, hiddenQuestions } = useNavigationStore();
  const { answers } = useResponseStore();

  if (!settings.showProgressBar) {
    return null;
  }

  const progressType = settings.progressBarType || 'percentage';

  // Progress reflects answered questions, not page position: a page-based
  // ratio (currentPage/totalPages) reads 100% on a single-page survey before
  // anything is answered. Hidden (logic-suppressed) questions are excluded so
  // the denominator matches what the respondent actually sees.
  const visibleQuestions = questions.filter((q) => !hiddenQuestions.has(q.code));
  const answeredCount = visibleQuestions.filter((q) =>
    isAnswered(answers[q.code]),
  ).length;
  const percentage =
    visibleQuestions.length === 0
      ? 0
      : Math.round((answeredCount / visibleQuestions.length) * 100);

  return (
    <div className={cn('w-full', className)}>
      {/* Progress bar */}
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full bg-primary-600 transition-all duration-300 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Progress text */}
      <div className="mt-2 flex justify-between text-xs text-gray-500">
        {progressType === 'pages' ? (
          <>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <span>{percentage}%</span>
          </>
        ) : (
          <span className="w-full text-center">{percentage}% complete</span>
        )}
      </div>
    </div>
  );
}
